import { type HTMLAttributes, type MouseEventHandler, type ReactNode, useId } from "react";
import { Icon } from "../icon/Icon";

export interface ListCellProps extends Omit<HTMLAttributes<HTMLDivElement>, "title" | "onClick"> {
  title: ReactNode;
  /** 줄을 덮는 버튼에 붙는다 — 그래서 대상은 div 가 아니라 button 이다. */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  description?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  chevron?: boolean;
}

/** List row with leading visual, title/description, and a trailing slot.
 *
 *  누를 수 있는 줄은 줄 전체를 덮는 **진짜 `<button>`** 을 뒤에 깔아서 만든다.
 *  div + onClick 이면 탭 이동에 잡히지도, Enter/Space 로 열리지도 않는다 —
 *  주문·메뉴판·메뉴 고르기가 전부 이 줄이라 키보드만 쓰는 사용자에게는 앱의
 *  절반이 잠긴다. div 에 `role="button"` 만 얹는 방법은 안 쓴다: trailing 슬롯에
 *  버튼·스위치가 들어와서 버튼 안에 버튼이 되고, 그건 더 나쁜 마크업이다.
 *
 *  덮개를 **뒤에** 깔면 trailing 컨트롤이 위에 남아 각자 눌린다 — 스위치를
 *  누르려다 줄이 열리는 일이 없어지고, 호출부의 `stopPropagation` 도 사라진다. */
export function ListCell({
  title,
  description,
  leading,
  trailing,
  chevron = false,
  onClick,
  className,
  style,
  ...rest
}: ListCellProps) {
  const clickable = !!onClick;
  const titleId = useId();

  return (
    <div
      // 누름·호버 피드백은 `.maky-row`(styles/maky-interactions.css) 한 곳에 있다.
      className={clickable ? `maky-row ${className ?? ""}` : className}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 8px",
        fontFamily: "var(--font-sans)",
        ...style,
      }}
      {...rest}
    >
      {clickable && (
        <button
          type="button"
          onClick={onClick}
          aria-labelledby={titleId}
          // 배경 덮개라 크기가 줄어드는 게 안 보인다. 피드백은 `.maky-row` 담당.
          data-press="none"
          style={{
            position: "absolute",
            inset: 0,
            border: "none",
            background: "transparent",
            borderRadius: "inherit",
            cursor: "pointer",
          }}
        />
      )}
      {leading && <div style={{ flex: "none", display: "flex", alignItems: "center" }}>{leading}</div>}
      {/* 덮개가 뒤에 있으므로 글자는 클릭을 통과시킨다. */}
      <div style={{ flex: 1, minWidth: 0, pointerEvents: "none" }}>
        <div
          id={titleId}
          style={{
            fontSize: "var(--font-size-body-2)",
            lineHeight: "var(--line-height-body-2)",
            fontWeight: 600,
            color: "var(--label-normal)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </div>
        {description && (
          <div
            style={{
              fontSize: "var(--font-size-label-2)",
              lineHeight: "var(--line-height-label-2)",
              color: "var(--label-alternative)",
              marginTop: 2,
            }}
          >
            {description}
          </div>
        )}
      </div>
      {trailing && (
        <div
          style={{
            position: "relative",
            flex: "none",
            color: "var(--label-alternative)",
            fontSize: "var(--font-size-label-1)",
          }}
        >
          {trailing}
        </div>
      )}
      {chevron && (
        <Icon
          name="chevronRight"
          size={20}
          style={{ flex: "none", color: "var(--label-assistive)", pointerEvents: "none" }}
        />
      )}
    </div>
  );
}

export default ListCell;
